import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Delete } from 'lucide-react';

const LoginModalContext = createContext(null);

const INTENT_LABELS = {
    pay: { title: 'Inicia sesión para pagar', desc: 'Accede con tu nickname y PIN de 4 dígitos.' },
    favorite: { title: 'Inicia sesión para guardar favoritos', desc: 'Accede con tu nickname y PIN de 4 dígitos.' },
    offer: { title: 'Inicia sesión para hacer una oferta', desc: 'Accede con tu nickname y PIN de 4 dígitos.' },
    comment: { title: 'Inicia sesión para comentar', desc: 'Accede con tu nickname y PIN de 4 dígitos.' },
    generic: { title: 'Inicia sesión en Ok te lo vendo', desc: 'Accede con tu nickname y PIN de 4 dígitos.' },
};

export const LoginModalProvider = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [intent, setIntent] = useState('generic');
    const [nickname, setNickname] = useState('');
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const resolveRef = useRef(null);
    const { loginUser } = useAuth();

    const promptLogin = useCallback((options = {}) => {
        setIntent(options.intent || 'generic');
        setNickname('');
        setPin('');
        setOpen(true);
        return new Promise((resolve) => { resolveRef.current = resolve; });
    }, []);

    const finish = (success) => {
        setOpen(false);
        setPin('');
        if (resolveRef.current) {
            resolveRef.current(success);
            resolveRef.current = null;
        }
    };

    const submit = async () => {
        if (!nickname.trim()) { toast.error('Ingresa tu nickname'); return; }
        if (pin.length !== 4) { toast.error('El PIN debe tener 4 dígitos'); return; }
        setBusy(true);
        try {
            await loginUser(nickname.trim().replace('@', ''), pin);
            toast.success(`¡Hola @${nickname}! Sesión iniciada.`);
            finish(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Nickname o PIN incorrecto');
            setPin('');
        } finally {
            setBusy(false);
        }
    };

    const onKeypadPress = (n) => {
        if (pin.length >= 4) return;
        const newPin = pin + String(n);
        setPin(newPin);
        if (newPin.length === 4) {
            // auto-submit
            setTimeout(() => {
                if (nickname.trim()) {
                    (async () => {
                        setBusy(true);
                        try {
                            await loginUser(nickname.trim().replace('@', ''), newPin);
                            toast.success(`¡Hola @${nickname}!`);
                            finish(true);
                        } catch (e) {
                            toast.error(e?.response?.data?.detail || 'Nickname o PIN incorrecto');
                            setPin('');
                        } finally {
                            setBusy(false);
                        }
                    })();
                }
            }, 200);
        }
    };

    const meta = INTENT_LABELS[intent] || INTENT_LABELS.generic;

    return (
        <LoginModalContext.Provider value={{ promptLogin }}>
            {children}
            <Dialog open={open} onOpenChange={(v) => { if (!v) finish(false); else setOpen(true); }}>
                <DialogContent className="sm:max-w-md" data-testid="login-modal">
                    <DialogHeader>
                        <div className="mx-auto mb-2">
                            <img src="/assets/oklovendo-logo.png" alt="Ok te lo vendo" className="h-10 mx-auto object-contain" onError={e => { e.target.style.display='none'; }} />
                        </div>
                        <DialogTitle className="text-center">{meta.title}</DialogTitle>
                        <DialogDescription className="text-center">{meta.desc}</DialogDescription>
                        <p className="text-center text-xs text-amber-700 font-medium pt-1">
                            Tu OkUser/Nickname es sensible a las mayúsculas y minúsculas
                        </p>
                    </DialogHeader>
                    <div className="space-y-3 pt-2">
                        <div>
                            <Label htmlFor="lm-nick">Nickname / OkUser</Label>
                            <Input
                                id="lm-nick"
                                value={nickname}
                                onChange={(e) => setNickname(e.target.value.replace('@', ''))}
                                placeholder="tu_nickname"
                                autoFocus
                                data-testid="login-modal-nickname"
                                onKeyDown={(e) => e.key === 'Enter' && pin.length === 4 && submit()}
                            />
                        </div>
                        <div>
                            <Label>PIN (4 dígitos)</Label>
                            <div className="flex gap-2 justify-center mt-1">
                                {[0, 1, 2, 3].map(i => (
                                    <div key={i} className={`h-3 w-3 rounded-full border ${pin.length > i ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))]' : 'bg-transparent'}`} />
                                ))}
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-3 max-w-[240px] mx-auto">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                                    <Button key={n} type="button" variant="outline" className="h-11" onClick={() => onKeypadPress(n)} data-testid={`login-modal-key-${n}`}>{n}</Button>
                                ))}
                                <Button type="button" variant="ghost" className="h-11" onClick={() => setPin('')} data-testid="login-modal-key-c">C</Button>
                                <Button type="button" variant="outline" className="h-11" onClick={() => onKeypadPress(0)} data-testid="login-modal-key-0">0</Button>
                                <Button type="button" variant="ghost" className="h-11" onClick={() => setPin(pin.slice(0, -1))} data-testid="login-modal-key-back">
                                    <Delete className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        <Button className="w-full" onClick={submit} disabled={busy || !nickname.trim() || pin.length !== 4} data-testid="login-modal-submit">
                            {busy ? 'Ingresando…' : 'Entrar'}
                        </Button>
                        <div className="text-center text-xs text-muted-foreground">
                            ¿Es tu primer acceso? Usa el PIN inicial <strong>9999</strong>.
                        </div>
                        <div className="border-t pt-3 text-center space-y-2">
                            <p className="text-xs text-muted-foreground">Si aún no tienes cuenta en okgo, regístrate, es muy fácil</p>
                            <a href="https://app.okgo.mx/signup?q=signup&type=Cliente&step=1" target="_blank" rel="noreferrer" className="block">
                                <button type="button" className="w-full rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2 px-4 transition-colors">
                                    Regístrate
                                </button>
                            </a>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </LoginModalContext.Provider>
    );
};

export const usePromptLogin = () => {
    const ctx = useContext(LoginModalContext);
    if (!ctx) throw new Error('usePromptLogin must be used within LoginModalProvider');
    return ctx.promptLogin;
};
